import { Typography, Card, Breadcrumb, Table } from 'antd';
import { Link } from 'react-router-dom';
import { HomeOutlined } from '@ant-design/icons';
import './LegalPages.css';

const { Title, Paragraph, Text } = Typography;

const Section = ({ title, children }) => (
  <>
    <Title level={4} style={{ color: 'var(--wl-primary)', marginTop: 32 }}>{title}</Title>
    {children}
  </>
);

const miniTable = (columns, data) => (
  <Table
    columns={columns}
    dataSource={data}
    pagination={false}
    bordered
    size="small"
    style={{ marginBottom: 16 }}
    scroll={{ x: 'max-content' }}
    rowKey={(_, i) => i}
  />
);

const PrivacyPolicy = () => (
  <div className="legal-page">
    <div className="legal-page-inner">
      <Breadcrumb items={[
        { title: <Link to="/login"><HomeOutlined /> Home</Link> },
        { title: 'Privacy Policy' },
      ]} style={{ marginBottom: 24 }} />

      <Card bordered={false} className="legal-card">
        <Typography>
          <Title level={2} style={{ color: 'var(--wl-primary)', marginBottom: 4 }}>WeCircle — Privacy Policy</Title>
          {/* 1 */}
          <Section title="1. Introduction">
            <Paragraph>
              We Little (&quot;We,&quot; &quot;Us,&quot; or &quot;Our&quot;) operates WeCircle — a clinic-centred paediatric oral health management platform.
              This Privacy Policy explains how We Little collects, uses, stores, shares, and protects personal data in connection with the
              WeCircle mobile application and all associated platform interfaces, including the Clinic Web Application, Super Admin Portal,
              and School Administrator Portal.
            </Paragraph>
            <Paragraph>
              We are committed to protecting your personal data in accordance with the Digital Personal Data Protection Act, 2023
              (&quot;DPDP Act&quot;) and all other applicable Indian laws and regulations.
            </Paragraph>
            <Paragraph>
              By using the Application or any associated WeCircle service, you acknowledge that you have read and understood this Privacy Policy.
            </Paragraph>
          </Section>

          {/* 2 */}
          <Section title="2. Who We Are — Data Fiduciary">
            <Paragraph>
              We Little is the Data Fiduciary for all personal data processed through the WeCircle platform. As a Data Fiduciary,
              We Little determines the purposes and means of processing your personal data and is responsible for ensuring that
              processing is conducted lawfully, fairly, and transparently.
            </Paragraph>
            <Paragraph>
              WeCircle is developed and maintained on behalf of We Little by PPTS (India) Pvt. Ltd. (&quot;Technology Partner&quot;),
              headquartered at 7th Floor, KGISL SEZ Tech Park, Saravanampatti, Coimbatore – 641035. The Technology Partner processes
              data solely as a Data Processor acting under the instructions of We Little and does not use personal data for any
              independent purpose.
            </Paragraph>
          </Section>

          {/* 3 */}
          <Section title="3. What Personal Data We Collect">
            <Title level={5} style={{ marginTop: 16 }}>3.1 Parent / Guardian Data</Title>
            {miniTable(
              [
                { title: 'Data Element', dataIndex: 'element', width: 180 },
                { title: 'Purpose', dataIndex: 'purpose', width: 300 },
              ],
              [
                { element: 'Registered mobile number', purpose: 'OTP-based authentication and app download invitation via SMS' },
                { element: 'Name', purpose: 'Account identification and profile management' },
                { element: 'Clinic association', purpose: 'Determining which clinic contact numbers are displayed to the parent' },
              ],
            )}

            <Title level={5} style={{ marginTop: 16 }}>3.2 Child Health Data</Title>
            <Paragraph>
              Child health data is processed on behalf of the registered Doctor and associated clinic. It is classified as sensitive
              personal data and receives heightened protection throughout the platform.
            </Paragraph>
            {miniTable(
              [
                { title: 'Data Element', dataIndex: 'element', width: 180 },
                { title: 'Purpose', dataIndex: 'purpose', width: 300 },
              ],
              [
                { element: 'Child\'s name, date of birth, gender', purpose: 'Child profile creation and identification' },
                { element: 'School association tag', purpose: 'Institutional roster management for School Administrator portal' },
                { element: 'Clinical photographs (JPG/JPEG)', purpose: 'Clinical media linked to the child\'s dental record' },
                { element: 'Smile Passport records (diagnosis, clinical notes, treatment observations)', purpose: 'Delivery of clinical health records to the authorised parent via the mobile application' },
              ],
            )}

            <Title level={5} style={{ marginTop: 16 }}>3.3 Doctor and Clinical Staff Data</Title>
            {miniTable(
              [
                { title: 'Data Element', dataIndex: 'element', width: 180 },
                { title: 'Purpose', dataIndex: 'purpose', width: 300 },
              ],
              [
                { element: 'Name, email address', purpose: 'Account authentication and audit attribution' },
                { element: 'Clinic association', purpose: 'Role-scoped data access enforcement' },
                { element: 'Action logs with timestamp', purpose: 'Platform-wide audit trail and clinical governance' },
              ],
            )}

            <Title level={5} style={{ marginTop: 16 }}>3.4 Automatically Collected Data</Title>
            {miniTable(
              [
                { title: 'Data Element', dataIndex: 'element', width: 180 },
                { title: 'Purpose', dataIndex: 'purpose', width: 300 },
              ],
              [
                { element: 'Session activity logs', purpose: 'Security, session management, and audit trail' },
                { element: 'Device type and OS version', purpose: 'Application compatibility and error diagnostics' },
              ],
            )}
            <Paragraph>
              We do not collect financial data, payment information, or government-issued identity numbers through the WeCircle platform.
            </Paragraph>
          </Section>

          {/* 4 */}
          <Section title="4. How We Collect Data">
            <ul>
              <li><Text strong>Account provisioning:</Text> Parent accounts are created by authorised Doctors or platform administrators (Super Admins). Self-registration is not permitted on this platform.</li>
              <li><Text strong>Direct clinical input:</Text> Child profiles, clinical media, and Smile Passport records are entered manually by registered Doctors through the Clinic Web Application.</li>
              <li><Text strong>Automatic collection:</Text> Session activity, authentication events, and platform audit logs are generated automatically upon platform use.</li>
            </ul>
          </Section>

          {/* 5 */}
          <Section title="5. Lawful Basis for Processing">
            <Paragraph>We process personal data on the following lawful grounds under the DPDP Act, 2023 and applicable law:</Paragraph>
            <ul>
              <li><Text strong>Consent:</Text> Where a parent or guardian provides consent upon account activation and first use of the Application.</li>
              <li><Text strong>Legitimate interest:</Text> For audit trail generation, session security, and platform integrity.</li>
              <li><Text strong>Legal obligation:</Text> Where processing is required to comply with applicable Indian law or regulatory requirement.</li>
            </ul>
            <Paragraph>
              Personal data of children (individuals below 18 years of age) is processed only where verifiable consent has been
              obtained from the parent or legal guardian. The parent or guardian who activates the WeCircle account is taken to
              have provided this consent on the child&apos;s behalf at the point of first use.
            </Paragraph>
          </Section>

          {/* 6 */}
          <Section title="6. Children&apos;s Personal Data">
            <Paragraph>
              WeCircle is a paediatric health platform. All child health records processed through the platform relate to
              individuals below 18 years of age and are therefore classified as sensitive personal data subject to heightened protection.
            </Paragraph>
            <ul>
              <li>Child records are created exclusively by authorised Doctors.</li>
              <li>Clinical data is visible only to the child&apos;s assigned Doctor, the authorised parent or guardian, and platform administrators within their defined scope.</li>
              <li>Child health records are not shared with any third party for commercial, marketing, profiling, or research purposes.</li>
              <li>No automated decision-making or algorithmic profiling is applied to any child&apos;s data through the WeCircle platform.</li>
            </ul>
          </Section>

          {/* 7 */}
          <Section title="7. Who Can Access Your Data">
            <Paragraph>
              Access to personal data within the WeCircle platform is strictly role-based and system-enforced.
              The following access boundaries apply across all platform interfaces:
            </Paragraph>
            {miniTable(
              [
                { title: 'Role', dataIndex: 'role', width: 140 },
                { title: 'Data Access Scope', dataIndex: 'scope', width: 340 },
              ],
              [
                { role: 'Parent / Guardian', scope: 'Own account profile; published Smile Passport(s) of their registered child only' },
                { role: 'Doctor', scope: 'Child profiles and Smile Passports of children assigned to them only' },
                { role: 'School Administrator', scope: 'Read-only student roster for their institution — no clinical data accessible' },
                { role: 'Super Admin', scope: 'Platform-wide administrative access within MVP scope' },
                { role: 'Technology Partner (PPTS)', scope: 'Infrastructure-level access only, under contractual confidentiality obligations' },
              ],
            )}
            <Paragraph>
              No role can access data beyond its defined boundary. These restrictions are architecturally enforced at the system
              level and cannot be bypassed through any in-application action.
            </Paragraph>
          </Section>

          {/* 8 */}
          <Section title="8. Data Sharing and Third Parties">
            <Paragraph>
              We Little does not sell, rent, or trade any personal data to third parties under any circumstances.
            </Paragraph>
            <Paragraph>
              We may share personal data with the following categories of third-party service providers, solely to operate
              and maintain the WeCircle platform:
            </Paragraph>
            <ul>
              <li><Text strong>Cloud infrastructure providers</Text> — for hosting backend services, APIs, and data storage</li>
              <li><Text strong>SMS gateway providers</Text> — for OTP delivery and app download invitation SMS to parents</li>
              <li><Text strong>Email service providers</Text> — for transactional notifications to clinical staff</li>
            </ul>
            <Paragraph>
              All third-party service providers are bound by contractual data processing obligations and are not permitted
              to use your data for any purpose beyond providing the contracted service to We Little.
            </Paragraph>
            <Paragraph>
              We may also disclose personal data where required to do so by applicable law, court order, or the direction
              of a competent regulatory authority.
            </Paragraph>
          </Section>

          {/* 9 */}
          <Section title="9. Data Security">
            <Paragraph>
              We implement appropriate technical and organisational measures to protect personal data against unauthorised access,
              disclosure, loss, alteration, or destruction. These measures include:
            </Paragraph>
            <ul>
              <li>Role-based access control enforced at the architectural level across all platform interfaces</li>
              <li>Automatic session expiry after 15 minutes of inactivity across all authenticated sessions</li>
              <li>Encrypted data transmission (HTTPS) across all platform interfaces and API endpoints</li>
              <li>Comprehensive audit trail logging capturing all significant platform actions with user attribution, role, clinic association, and timestamp</li>
            </ul>
            <Paragraph>
              While we take all reasonable precautions, no data transmission or storage system can be guaranteed to be completely
              secure at all times. In the event of a personal data breach that is likely to result in a risk to your rights,
              we will notify you and the applicable regulatory authority in accordance with our obligations under the DPDP Act, 2023.
            </Paragraph>
          </Section>

          {/* 10 */}
          <Section title="10. Data Retention">
            <Paragraph>
              We retain personal data only for as long as necessary to fulfil the purposes for which it was collected,
              or as required by applicable law.
            </Paragraph>
            {miniTable(
              [
                { title: 'Data Category', dataIndex: 'category', width: 180 },
                { title: 'Retention Approach', dataIndex: 'approach', width: 300 },
              ],
              [
                { category: 'Child health records (Smile Passport)', approach: 'Retained for the duration of the clinical relationship and as required by applicable medical record retention laws' },
                { category: 'Parent account data', approach: 'Retained while the account is active; subject to erasure on verified written request, subject to legal retention obligations' },
                { category: 'Platform audit logs', approach: 'Retained in accordance with applicable compliance and legal requirements' },
                { category: 'Session data', approach: 'Automatically purged upon session expiry or timeout' },
              ],
            )}
            <Paragraph>
              Data that is no longer required for its original purpose and is not subject to a legal retention obligation
              will be securely deleted or irreversibly anonymised.
            </Paragraph>
          </Section>

          {/* 11 */}
          <Section title="11. Your Rights Under the DPDP Act, 2023">
            <Paragraph>
              As a Data Principal under the Digital Personal Data Protection Act, 2023, you have the following rights
              with respect to your personal data:
            </Paragraph>
            <ul>
              <li><Text strong>Right of Access:</Text> You may request confirmation of whether We Little processes your personal data and obtain a summary of the data processed and the purposes for which it is used.</li>
              <li><Text strong>Right to Correction and Erasure:</Text> You may request that inaccurate or incomplete personal data be corrected, or that personal data no longer necessary for the stated purpose be erased, subject to applicable legal retention obligations.</li>
              <li><Text strong>Right to Grievance Redressal:</Text> You have the right to raise a grievance with our designated Grievance Officer regarding any aspect of our data processing practices.</li>
              <li><Text strong>Right to Nominate:</Text> You may nominate an individual to exercise your rights on your behalf in the event of your death or incapacity, in accordance with the DPDP Act.</li>
            </ul>
            <Paragraph>
              To exercise any of the above rights, contact our Grievance Officer using the details provided in Section 12.
              We will respond to all verified requests within the timelines prescribed under applicable law.
            </Paragraph>
            <Paragraph>
              <Text strong>Note:</Text> Certain data — particularly child clinical records — may be subject to statutory medical record
              retention obligations that limit or delay the right to erasure. We will inform you of any such limitation at the time of your request.
            </Paragraph>
          </Section>

          {/* 12 */}
          <Section title="12. Grievance Officer">
            <Paragraph>
              In accordance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021
              and the Digital Personal Data Protection Act, 2023, We Little has designated a Grievance Officer to address any
              concerns or complaints regarding the processing of your personal data.
            </Paragraph>
            {miniTable(
              [
                { title: '', dataIndex: 'label', width: 120, render: (v) => <Text strong>{v}</Text> },
                { title: '', dataIndex: 'value', width: 360 },
              ],
              [
                { label: 'Name', value: '[INSERT GRIEVANCE OFFICER NAME]' },
                { label: 'Designation', value: '[INSERT DESIGNATION]' },
                { label: 'Organisation', value: 'We Little' },
                { label: 'Email', value: '[INSERT GRIEVANCE EMAIL ADDRESS]' },
                { label: 'Phone', value: '[INSERT GRIEVANCE PHONE NUMBER]' },
                { label: 'Address', value: '739, Avinashi Road, 3rd Floor, Sri Ram Towers, Near Anna Statue, Coimbatore – 641018' },
              ],
            )}
            <Paragraph>
              You may raise a grievance by writing to the above contact. We will acknowledge receipt within [X] business days
              and endeavour to resolve the matter within 30 days of receipt unless additional time is required, in which case
              we will notify you of the reason and expected resolution timeline.
            </Paragraph>
            <Paragraph>
              If you are not satisfied with our response, you may escalate the matter to the Data Protection Board of India,
              once constituted and operational under the DPDP Act, 2023.
            </Paragraph>
          </Section>

          {/* 13 */}
          <Section title="13. Changes to This Privacy Policy">
            <Paragraph>
              We Little may update this Privacy Policy from time to time to reflect changes in applicable law, platform
              functionality, or our data processing practices. The updated Policy will be made available within the Application
              and on our website. Where changes are material, we will make reasonable efforts to notify you directly through
              the Application. The version number and effective date at the top of this document will always reflect the current
              Policy. Continued use of the Application following any update constitutes your acknowledgment of the revised Policy.
            </Paragraph>
          </Section>

          {/* 14 */}
          <Section title="14. Contact Us">
            <Paragraph>
              For all privacy-related enquiries not addressed through the Grievance Officer process, contact us at:
            </Paragraph>
            <Paragraph>
              <Text strong>We Little</Text><br />
              739, Avinashi Road, 3rd Floor, Sri Ram Towers, Near Anna Statue, Coimbatore – 641018<br />
              Email: [INSERT CONTACT EMAIL]<br />
              Phone: [INSERT CONTACT NUMBER]
            </Paragraph>
          </Section>

          <div style={{ textAlign: 'center', marginTop: 40, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              WeCircle — Privacy Policy &nbsp;|&nbsp; We Little &nbsp;|&nbsp; Version 1.0
            </Text>
          </div>
        </Typography>
      </Card>
    </div>
  </div>
);

export default PrivacyPolicy;
